import { ChevronDown } from "lucide-react";

import * as React from "react";

import * as RPNInput from "react-phone-number-input";

import flags from "react-phone-number-input/flags";

import { Input, InputProps } from "./input";

import { cn } from "./utils";

type PhoneInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> &
    Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
        onChange?: (value: RPNInput.Value | undefined) => void;
    };

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<
    React.ElementRef<typeof RPNInput.default>,
    PhoneInputProps
>(({ className, onChange, ...props }, ref) => {
    return (
        <RPNInput.default
            ref={ref}
            className={cn("flex", className)}
            flagComponent={FlagComponent}
            countrySelectComponent={CountrySelect}
            inputComponent={InputComponent}
            smartCaret={false}
            onChange={(value: RPNInput.Value | undefined) => onChange?.(value)}
            {...props}
        />
    );
});
PhoneInput.displayName = "PhoneInput";

const InputComponent = React.forwardRef<HTMLInputElement, InputProps>(({ className, ...props }, ref) => (
    <Input className={cn("z-10 relative flex-1 rounded-e-lg rounded-s-none min-w-0", className)} {...props} ref={ref} />
));
InputComponent.displayName = "InputComponent";

type CountrySelectOption = { label: string; value: RPNInput.Country };

type CountrySelectProps = {
    disabled?: boolean;
    value?: RPNInput.Country;
    onChange: (value: RPNInput.Country | undefined) => void;
    options: CountrySelectOption[];
} & Omit<React.ComponentPropsWithoutRef<"select">, "value" | "onChange" | "children">;

const CountrySelect = ({ disabled, value, onChange, options, ...props }: CountrySelectProps) => {
    const validOptions = React.useMemo(() => options.filter((option) => option.value), [options]);
    const selectedCountryLabel = validOptions.find((option) => option.value === value)?.label ?? value ?? undefined;

    const handleSelect = React.useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            onChange((event.target.value || undefined) as RPNInput.Country | undefined);
        },
        [onChange]
    );

    return (
        <div
            className={cn(
                "z-0 relative flex justify-center items-center gap-1 px-2 pr-2 border border-slate-300 dark:border-slate-500 border-r-0 rounded-e-none rounded-s-lg w-14 h-12 shrink-0",
                disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            )}
        >
            <FlagComponent country={value} countryName={selectedCountryLabel ?? value} />
            <ChevronDown className="opacity-75 w-4 h-4 shrink-0" />
            <select
                {...props}
                value={value ?? ""}
                onChange={handleSelect}
                disabled={disabled}
                aria-label="Select country"
                className="absolute inset-0 opacity-0 pr-2 cursor-[inherit]"
            >
                <option value="">Select country</option>
                {validOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                        {`${option.label} +${RPNInput.getCountryCallingCode(option.value)}`}
                    </option>
                ))}
            </select>
        </div>
    );
};

const FlagComponent = ({ country, countryName }: { country?: RPNInput.Country; countryName?: string }) => {
    const Flag = country ? flags[country] : undefined;

    return (
        <span className="flex rounded-sm w-6 h-4 [&_svg]:size-full overflow-hidden">
            {Flag && <Flag title={countryName ?? ""} />}
        </span>
    );
};
FlagComponent.displayName = "FlagComponent";

export { PhoneInput };
